import { useState, useEffect } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { useAccountCreationContext } from "@/components/signup/account-creation";
import { emailIsValid, fontSize } from "@/components/signup/utils";
import { wacToken } from "@/libraries/design/foundations";

import { InlineInput, InlineSeparator, InlineSubmit, SocialButton } from "./";

export const InlineInputSubmitSocial = () => {
    const intl = useIntl();
    const { isReadyForInvalid, setIsEmailValid, typeAheadEmail } =
        useAccountCreationContext();
    const [email, setEmail] = useState(typeAheadEmail);
    const [isValid, setValid] = useState<boolean>();

    const helpText = intl.formatMessage({
        id: "signupForm.accountCreation.helperText",
        defaultMessage:
            "Using a work email helps find teammates and boost collaboration."
    });

    const errorMessageText = intl.formatMessage({
        id: "BxpUiSignup.SignupForm.invalidEmail",
        defaultMessage: "Please enter a valid email address."
    });

    const placeholderText = intl.formatMessage({
        id: "BxpUiSignup.SignupForm.workEmail",
        defaultMessage: "Work email"
    });

    useEffect(() => {
        const valid = emailIsValid(email);

        if (!valid && !isReadyForInvalid) {
            setValid(undefined);
            return;
        }

        setValid(valid);
        setIsEmailValid(valid);
    }, [email, setIsEmailValid, isReadyForInvalid]);

    return (
        <div css={containerCss}>
            <label css={[labelCss, helpTextContainerCss]}>
                {placeholderText}
            </label>
            <div css={inputSubmitSocialContainerCss}>
                <div
                    css={[
                        inputSubmitGroupCss,
                        isValid === undefined && inputSubmitGroupFocusCss,
                        isValid === false && inlineInputInvalidCss,
                        isValid === true && inputValidCss
                    ]}
                >
                    <div css={inputCss}>
                        <InlineInput
                            email={email}
                            setEmail={setEmail}
                            isValid={isValid}
                            setValid={setValid}
                            errorMessageText={errorMessageText}
                        />
                        <div css={[helpTextContainerCss, helpTextMobileCss]}>
                            <label
                                css={[helpTextCss, helpErrorTextMobileCss]}
                                htmlFor="inline_email_embedded_signup_input"
                            >
                                {helpText}
                            </label>
                            {isValid === false ? (
                                <div
                                    css={[
                                        helpErrorTextMobileCss,
                                        errorMessageCss
                                    ]}
                                    role="alert"
                                >
                                    {errorMessageText}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <InlineSubmit isValid={isValid} />
                </div>
                <InlineSeparator />
                <SocialButton />
            </div>
            <div css={helpTextContainerCss}>
                <label
                    css={[helpTextCss, helpErrorTextDesktopCss]}
                    htmlFor="inline_email_embedded_signup_input"
                >
                    {helpText}
                </label>
                {isValid === false ? (
                    <div
                        css={[helpErrorTextDesktopCss, errorMessageCss]}
                        role="alert"
                    >
                        {errorMessageText}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const containerCss = css({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px"
});

const labelCss = css({
    color: "inherit",
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: 400,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- hide asterisk
    ["[aria-hidden=true]"]: {
        display: "none"
    }
});

const inputSubmitSocialContainerCss = css({
    alignItems: "center",
    display: "flex",
    gap: token("space.200"),
    width: "100%",
    [media.below.sm]: {
        flexDirection: "column",
        gap: token("space.150")
    }
});

const inputSubmitGroupCss = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.200"),
    width: "100%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["button"]: {
        minHeight: "auto !important"
    },
    [media.above.sm]: {
        height: "48px",
        boxSizing: "border-box",
        alignItems: "center",
        // eslint-disable-next-line local/ensure-wac-token-usage
        border: "1px solid #B7B9BE",
        borderRadius: 40,
        flexDirection: "row",
        gap: token("space.150")
    }
});

const inputSubmitGroupFocusCss = css({
    [media.above.sm]: {
        ["&:focus-within"]: {
            // eslint-disable-next-line local/ensure-wac-token-usage
            boxShadow: "inset 0 0 0 1px var(--Extended-Blues-Blue600, #357DE8)",
            // eslint-disable-next-line local/ensure-wac-token-usage
            borderColor: "var(--Extended-Blues-Blue600, #357DE8)"
        }
    }
});

const inputCss = css({
    flexGrow: 1,
    width: "100%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["label"]: {
        margin: "0 auto"
    }
});

const helpTextContainerCss = css({
    marginInlineStart: "24px",
    marginInlineEnd: "24px"
});

const helpTextMobileCss = css({
    [media.below.sm]: {
        marginBlockStart: "4px"
    }
});

const helpTextCss = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "14px",
    lineHeight: 1.5,
    marginBlockStart: 6,
    opacity: 0.8
});

const helpErrorTextMobileCss = css({
    [media.above.sm]: {
        display: "none"
    }
});

const helpErrorTextDesktopCss = css({
    [media.below.sm]: {
        display: "none"
    }
});

const errorMessageCss = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#AE2A19",
    fontSize: fontSize.xs,
    lineHeight: 1.5
});

const inlineInputInvalidCss = css({
    [media.above.sm]: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        borderColor: "var(--Secondary-Secondary-Red, #AE2A19)",
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "inset 0 0 0 1px var(--Secondary-Secondary-Red, #AE2A19)"
    }
});

const inputValidCss = css({
    [media.above.sm]: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        borderColor: "var(--Extended-Limes-Lime600, #6A9A23)",
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "inset 0 0 0 1px var(--Extended-Limes-Lime600, #6A9A23)"
    }
});
